/**
 * Login Page — shown when auth is enabled and user is not authenticated.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';

/** Ein Ziel aus `GET /api/downloads/manifest` → `targets` (Wahl trifft der Server, siehe server/lib/desktop-downloads.ts). */
interface DownloadTarget {
  id: 'mac-arm64' | 'mac-x64' | 'windows' | 'linux';
  label: string;
  available: boolean;
  file: string | null;
  url: string | null;
  version: string | null;
  date: string | null;
  size: number | null;
}

/**
 * Read a `?next=` redirect target from the current URL and return it ONLY if it
 * points at a same-origin decision document or the partner platform (`partner.werking.tools` or any
 * `<port>.partner.werking.tools` dev-port subdomain) over HTTPS. Anything else
 * (open-redirect attempts, other hosts) is rejected → returns null.
 *
 * Why this exists: the nginx auth gate for `<port>.partner.werking.tools` 302s
 * unauthenticated requests to `partner.werking.tools/?next=<original-url>`. The
 * browser panel (Electron `persist:browser` partition) hits this when loading a
 * dev-port app. After the user logs in here the auth cookie IS set in that
 * partition — but without honouring `?next` the SPA just shows the CUI app and
 * the dev-port target is never re-loaded, so the panel appears stuck on the
 * login screen. Honouring `?next` navigates the webview to the now-authenticated
 * dev-port URL.
 */
function getSafeNextUrl(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get('next');
    if (!raw) return null;
    const url = new URL(raw, window.location.href);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (url.username || url.password) return null;
    if (url.origin === window.location.origin && url.pathname.startsWith('/downloads/entscheidungen/')) return url.toString();
    const host = url.hostname;
    if (host !== 'partner.werking.tools' && !host.endsWith('.partner.werking.tools')) return null;
    return url.toString();
  } catch {
    return null;
  }
}

type ClientPlatform = { os: 'mac' | 'windows' | 'linux' | null; macArch: 'arm64' | 'x64' | null };

/**
 * Betriebssystem aus dem User-Agent; Mac-Architektur NUR aus User-Agent Client
 * Hints (Chromium). Safari und Firefox melden auch auf Apple Silicon
 * "MacIntel" — dort wird bewusst NICHT geraten (macArch null), beide
 * Mac-Buttons stehen dann gleichrangig da.
 */
async function detectPlatform(): Promise<ClientPlatform> {
  if (typeof navigator === 'undefined') return { os: null, macArch: null };
  const ua = navigator.userAgent;
  const os: ClientPlatform['os'] = /Android|iPhone|iPad/i.test(ua) ? null
    : /Mac/i.test(ua) ? 'mac' : /Win/i.test(ua) ? 'windows' : /Linux/i.test(ua) ? 'linux' : null;
  if (os !== 'mac') return { os, macArch: null };
  const uaData = (navigator as Navigator & {
    userAgentData?: { getHighEntropyValues?: (h: string[]) => Promise<{ architecture?: string }> };
  }).userAgentData;
  if (!uaData?.getHighEntropyValues) return { os, macArch: null };
  try {
    const { architecture } = await uaData.getHighEntropyValues(['architecture']);
    if (architecture === 'arm') return { os, macArch: 'arm64' };
    if (architecture === 'x86') return { os, macArch: 'x64' };
  } catch { /* keine Hints → nicht raten */ }
  return { os, macArch: null };
}

function isRecommended(id: DownloadTarget['id'], p: ClientPlatform): boolean {
  if (p.os === 'mac') return p.macArch === null ? id.startsWith('mac-') : id === `mac-${p.macArch}`;
  return p.os !== null && id === p.os;
}

function fmtSize(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(0) + ' MB';
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function DownloadButton({ target, accent }: { target: DownloadTarget; accent: boolean }) {
  if (!target.available || !target.url) {
    return (
      <div data-ai-id={`login-page-download-${target.id}`} style={{
        padding: '10px 12px',
        background: 'rgba(255,255,255,0.02)',
        border: '1px dashed rgba(255,255,255,0.08)',
        borderRadius: 10,
        color: 'rgba(255,255,255,0.25)',
        fontSize: 12,
        textAlign: 'center',
      }}>
        {target.label}<br/><span style={{ fontSize: 10 }}>nicht verfügbar</span>
      </div>
    );
  }
  const meta = [
    target.version ? `v${target.version}` : null,
    target.date ? fmtDate(target.date) : null,
    target.size != null ? fmtSize(target.size) : null,
  ].filter(Boolean).join(' · ');
  return (
    <a
      data-ai-id={`login-page-download-${target.id}`}
      href={target.url}
      title={target.file ?? undefined}
      style={{
        padding: '10px 12px',
        background: accent ? 'rgba(222,193,94,0.08)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${accent ? 'rgba(222,193,94,0.3)' : 'rgba(255,255,255,0.1)'}`,
        borderRadius: 10,
        color: '#ffffff',
        fontSize: 13,
        fontWeight: 500,
        textAlign: 'center',
        textDecoration: 'none',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = accent ? 'rgba(222,193,94,0.15)' : 'rgba(255,255,255,0.08)';
        e.currentTarget.style.borderColor = accent ? 'color-mix(in srgb, var(--tnf-dec15e) 50%, transparent)' : 'rgba(255,255,255,0.2)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = accent ? 'rgba(222,193,94,0.08)' : 'rgba(255,255,255,0.04)';
        e.currentTarget.style.borderColor = accent ? 'rgba(222,193,94,0.3)' : 'rgba(255,255,255,0.1)';
      }}
    >
      {target.label}
      {accent && <div style={{ fontSize: 10, color: 'color-mix(in srgb, var(--tnf-dec15e) 80%, transparent)', marginTop: 2 }}>empfohlen</div>}
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{meta}</div>
    </a>
  );
}

interface LoginInputProps {
  label: string;
  type: string;
  value: string;
  onChange: (val: string) => void;
  autoFocus?: boolean;
  placeholder?: string;
  marginBottom?: number;
  dataAiId?: string;
}

function LoginInput({ label, type, value, onChange, autoFocus, placeholder, marginBottom = 20, dataAiId }: LoginInputProps) {
  return (
    <div style={{ marginBottom }}>
      <label style={{
        display: 'block',
        fontSize: 13,
        fontWeight: 500,
        color: 'rgba(255,255,255,0.6)',
        marginBottom: 8,
      }}>
        {label}
      </label>
      <input
        data-ai-id={dataAiId}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required
        autoFocus={autoFocus}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '14px 16px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 12,
          color: '#ffffff',
          fontSize: 15,
          outline: 'none',
          boxSizing: 'border-box',
          transition: 'all 0.2s',
        }}
        onFocus={e => {
          e.target.style.borderColor = 'color-mix(in srgb, var(--tnf-dec15e) 50%, transparent)';
          e.target.style.boxShadow = '0 0 0 3px rgba(222,193,94,0.15)';
          e.target.style.background = 'rgba(255,255,255,0.06)';
        }}
        onBlur={e => {
          e.target.style.borderColor = 'rgba(255,255,255,0.1)';
          e.target.style.boxShadow = 'none';
          e.target.style.background = 'rgba(255,255,255,0.03)';
        }}
      />
    </div>
  );
}

export default function LoginPage() {
  const { login, partnerCui } = useAuth();
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadTargets, setDownloadTargets] = useState<DownloadTarget[]>([]);
  const [platform, setPlatform] = useState<ClientPlatform>({ os: null, macArch: null });

  useEffect(() => {
    fetch('/api/downloads/manifest')
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j?.available && Array.isArray(j.targets)) setDownloadTargets(j.targets); })
      .catch(() => {});
    detectPlatform().then(setPlatform).catch(() => {});
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(user, password);
    if (!result.ok) {
      setError(result.error || 'Login failed');
      setLoading(false);
      return;
    }

    // Login succeeded → the auth cookie is now set in this browsing context
    // (incl. the Electron browser-panel `persist:browser` partition). If we were
    // sent here by the dev-port auth gate, navigate to that target now that the
    // cookie is present. Keep `loading` true so the form stays disabled during
    // the full-page navigation.
    const next = getSafeNextUrl();
    if (next) {
      window.location.replace(next);
      return;
    }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--tnfh-0a0a0f)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      position: 'relative',
      overflow: 'hidden',
      padding: '1rem',
    }}>
      {/* Background Orbs */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute', top: '25%', left: '25%',
          width: 384, height: 384,
          background: 'rgba(222,193,94,0.08)',
          borderRadius: '50%', filter: 'blur(80px)',
        }} />
        <div style={{
          position: 'absolute', bottom: '25%', right: '25%',
          width: 384, height: 384,
          background: 'rgba(59,130,246,0.06)',
          borderRadius: '50%', filter: 'blur(80px)',
        }} />
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600, height: 600,
          background: 'radial-gradient(circle, rgba(222,193,94,0.04) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
      </div>

      <div style={{ maxWidth: 440, width: '100%', position: 'relative', zIndex: 10 }}>
        {/* Logo & Title */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80, height: 80,
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: '0 8px 32px rgba(222,193,94,0.2)',
            overflow: 'hidden',
          }}>
            <img
              src="/werking-logo.png"
              alt="WerkING"
              style={{ width: 80, height: 80, display: 'block' }}
            />
          </div>
          <h1 style={{
            fontSize: 30,
            fontWeight: 800,
            color: '#ffffff',
            marginBottom: 8,
            letterSpacing: '-0.02em',
          }}>
            Werk<span style={{ color: 'var(--tnf-dec15e)' }}>ING</span> {partnerCui ? "Partner" : "CUI Dev"}
          </h1>
          <p style={{
            fontSize: 14,
            color: 'rgba(255,255,255,0.4)',
          }}>
            {partnerCui ? "Partner Plattform" : "Entwicklungsumgebung · Anmeldung für deinen Dev-Arbeitsbereich"}
          </p>
        </div>

        {/* Login Card */}
        <form
          onSubmit={handleSubmit}
          style={{
            background: 'rgba(255,255,255,0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16,
            padding: 32,
            boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
          }}
        >
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 16,
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 12,
              marginBottom: 24,
              fontSize: 13,
              color: 'var(--tnf-fca5a5)',
            }}>
              {error}
            </div>
          )}

          <LoginInput
            label="Benutzer"
            type="text"
            value={user}
            onChange={setUser}
            autoFocus
            placeholder="user-id"
            marginBottom={20}
            dataAiId="login-page-user-input"
          />

          <LoginInput
            label="Passwort"
            type="password"
            value={password}
            onChange={setPassword}
            marginBottom={28}
            dataAiId="login-page-password-input"
          />

          {/* Submit Button */}
          <button
            data-ai-id="login-page-submit"
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading
                ? 'rgba(255,255,255,0.1)'
                : 'linear-gradient(135deg, var(--tnf-dec15e) 0%, var(--tnf-c9a83e) 100%)',
              color: loading ? 'rgba(255,255,255,0.4)' : 'var(--tnf-0a0a0f)',
              border: 'none',
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: loading ? 'none' : '0 4px 24px rgba(222,193,94,0.25)',
              letterSpacing: '0.02em',
            }}
            onMouseEnter={e => {
              if (!loading) {
                e.currentTarget.style.background = 'linear-gradient(135deg, var(--tnf-e8cc6a) 0%, var(--tnf-d4b348) 100%)';
                e.currentTarget.style.boxShadow = '0 6px 32px rgba(222,193,94,0.35)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={e => {
              if (!loading) {
                e.currentTarget.style.background = 'linear-gradient(135deg, var(--tnf-dec15e) 0%, var(--tnf-c9a83e) 100%)';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(222,193,94,0.25)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            {loading ? 'Anmeldung...' : 'Anmelden'}
          </button>
        </form>

        {/* Desktop Download Card */}
        {downloadTargets.some(t => t.available) && (
          <div style={{
            marginTop: 24,
            padding: 20,
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 16,
          }}>
            <div style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.7)',
              marginBottom: 4,
            }}>
              Lieber als Desktop-App?
            </div>
            <div style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.4)',
              marginBottom: 14,
            }}>
              Eine eigene Anwendung statt Browser-Tab. Auto-Reconnect, kein Tab-Throttling.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {downloadTargets.map(t => (
                <DownloadButton key={t.id} target={t} accent={isRecommended(t.id, platform)} />
              ))}
            </div>
            {platform.os === 'mac' && platform.macArch === null && (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 10 }}>
                Welcher Mac? Apple-Menü → „Über diesen Mac“: steht dort „Chip Apple M…“, ist es Apple Silicon; steht dort „Prozessor Intel…“, ist es Intel.
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <p style={{
          fontSize: 12,
          color: 'rgba(255,255,255,0.2)',
          textAlign: 'center',
          marginTop: 24,
        }}>
          Powered by <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>Werkingflow</span>
        </p>
      </div>
    </div>
  );
}
